import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  CircularProgress,
  Alert,
  Box,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  FormControlLabel,
  Switch,
  Divider,
  Stack,
  Chip
} from '@mui/material';
import api from '../utils/Api';

const ManageClockInExceptionModal = ({ open, onClose, school }) => {
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  // Exception specific fields
  const [isActive, setIsActive] = useState(false);
  const [customRadius, setCustomRadius] = useState('');

  // 🔄 Fetch Teachers & Exceptions
  const fetchTeachers = useCallback(async () => {
    if (!school?._id) return;
    setLoading(true);
    setError('');
    try {
      const res = await api.get(`/super-admin/schools/${school._id}/teachers`);
      setTeachers(res.data.teachers || []);
    } catch (err) {
      console.error('Error fetching teachers:', err);
      setError(err.response?.data?.message || 'Failed to load school teachers.');
    } finally {
      setLoading(false);
    }
  }, [school]);

  useEffect(() => {
    if (open && school) {
      fetchTeachers();
      // Reset selected teacher and states
      setSelectedTeacherId('');
      setIsActive(false);
      setCustomRadius('');
    }
  }, [open, school, fetchTeachers]);

  // When selected teacher changes, prefill states
  const handleTeacherChange = (e) => {
    const id = e.target.value;
    setSelectedTeacherId(id);
    const teacher = teachers.find(t => t._id === id);
    if (teacher && teacher.clockInException) {
      setIsActive(teacher.clockInException.isActive);
      setCustomRadius(teacher.clockInException.customRadius || '');
    } else {
      setIsActive(false);
      setCustomRadius('');
    }
  };

  const handlePresetRadius = (radius) => {
    setCustomRadius(radius.toString());
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!selectedTeacherId) {
      setError('Please select a teacher.');
      return;
    }

    setSubmitting(true);
    setError('');
    setSuccess('');

    try {
      const radiusVal = customRadius ? Number(customRadius) : undefined;
      const res = await api.post(`/super-admin/teachers/${selectedTeacherId}/exception`, {
        customRadius: radiusVal,
        isActive
      });

      setSuccess('Clock-in exception updated successfully.');
      
      // Update local state with the returned exception
      setTeachers(prev => prev.map(t => {
        if (t._id === selectedTeacherId) {
          return {
            ...t,
            clockInException: res.data.exception
          };
        }
        return t;
      }));

      setTimeout(() => {
        setSuccess('');
      }, 2000);
    } catch (err) {
      console.error(err);
      setError(err.response?.data?.message || 'Failed to update clock-in exception.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    if (submitting) return;
    setTeachers([]);
    setSelectedTeacherId('');
    setIsActive(false);
    setCustomRadius('');
    setError('');
    setSuccess('');
    onClose();
  };

  const selectedTeacher = teachers.find(t => t._id === selectedTeacherId);

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 'bold' }}>Manage Clock-In Exceptions</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Manage custom geofence clock-in override exceptions for teachers of <strong>{school?.name}</strong>.
            This allows a teacher to clock in even if they are outside the standard school compound boundaries.
          </Typography>

          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}

          {loading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={3}>
              <FormControl fullWidth required>
                <InputLabel id="select-teacher-label">Select Teacher</InputLabel>
                <Select
                  labelId="select-teacher-label"
                  value={selectedTeacherId}
                  label="Select Teacher"
                  onChange={handleTeacherChange}
                  disabled={submitting}
                >
                  {teachers.map((t) => (
                    <MenuItem key={t._id} value={t._id}>
                      {t.user?.name || 'Unknown Name'} ({t.user?.phone || 'No Phone'})
                    </MenuItem>
                  ))}
                  {teachers.length === 0 && (
                    <MenuItem disabled>No teachers found in this school</MenuItem>
                  )}
                </Select>
              </FormControl>

              {selectedTeacherId && (
                <>
                  <Divider />
                  
                  <Box>
                    <Typography variant="subtitle2" gutterBottom color="text.primary">
                      Status Overview:
                    </Typography>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography variant="body2">Current Exception Status:</Typography>
                      {selectedTeacher?.clockInException?.isActive ? (
                        <Chip label="Active Override" color="success" size="small" />
                      ) : (
                        <Chip label="No Active Override" color="default" size="small" />
                      )}
                      {selectedTeacher?.clockInException?.customRadius && (
                        <Chip 
                          label={`${selectedTeacher.clockInException.customRadius} meters`} 
                          color="info" 
                          size="small" 
                          variant="outlined" 
                        />
                      )}
                    </Stack>
                  </Box>

                  <FormControlLabel
                    control={
                      <Switch
                        checked={isActive}
                        onChange={(e) => setIsActive(e.target.checked)}
                        disabled={submitting}
                        color="success"
                      />
                    }
                    label={
                      <Box>
                        <Typography variant="subtitle2">Enable Override Exception</Typography>
                        <Typography variant="caption" color="text.secondary">
                          When enabled, this teacher bypasses default school coordinates check.
                        </Typography>
                      </Box>
                    }
                  />

                  {isActive && (
                    <Stack spacing={2}>
                      <TextField
                        fullWidth
                        label="Custom Geofence Radius (in meters)"
                        type="number"
                        placeholder="e.g. 50000"
                        value={customRadius}
                        onChange={(e) => setCustomRadius(e.target.value)}
                        disabled={submitting}
                        required
                        helperText="Provide a maximum radius limit in meters. Standard GPS accuracy allows up to 50,000 meters for broad region-wide bypass."
                      />

                      <Box>
                        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                          Suggested Presets:
                        </Typography>
                        <Stack direction="row" spacing={1}>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => handlePresetRadius(400)}
                            sx={{ textTransform: 'none' }}
                          >
                            400m (Close proximity)
                          </Button>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            onClick={() => handlePresetRadius(1000)}
                            sx={{ textTransform: 'none' }}
                          >
                            1km (Transit/Suburban)
                          </Button>
                          <Button 
                            variant="outlined" 
                            size="small" 
                            color="success"
                            onClick={() => handlePresetRadius(50000)}
                            sx={{ textTransform: 'none' }}
                          >
                            50km (Broad Bypass)
                          </Button>
                        </Stack>
                      </Box>
                    </Stack>
                  )}
                </>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={handleClose} disabled={submitting} variant="outlined">
            Close
          </Button>
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={submitting || !selectedTeacherId}
          >
            {submitting ? <CircularProgress size={24} color="inherit" /> : 'Save Exception'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ManageClockInExceptionModal;
